import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Clock, ArrowRight, User } from 'lucide-react';
import Navbar from '../components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  buying_guide: 'Buying Guide',
  selling_guide: 'Selling Guide',
  renting_guide: 'Renting Guide',
  market_trends: 'Market Trends',
  city_spotlight: 'City Spotlight',
  home_loans: 'Home Loans'
};

const CATEGORY_COLORS = {
  buying_guide: 'bg-blue-50 text-blue-700',
  selling_guide: 'bg-emerald-50 text-emerald-700',
  renting_guide: 'bg-amber-50 text-amber-700',
  market_trends: 'bg-purple-50 text-purple-700',
  city_spotlight: 'bg-rose-50 text-rose-700',
  home_loans: 'bg-cyan-50 text-cyan-700'
};

const Blog = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await axios.get(`${API}/blog`);
        setPosts(res.data);
      } catch (error) {
        console.error('Error fetching posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const estimateReadTime = (content) => {
    const words = content?.split(/\s+/).length || 0;
    return Math.max(1, Math.ceil(words / 200));
  };

  return (
    <div className="min-h-screen bg-white pt-16">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-b from-slate-50 to-white px-6 md:px-12 lg:px-24 pt-12 pb-4">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" data-testid="blog-title">
            PROP<span className="font-normal">TICK</span> Blog
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Expert insights on Kerala real estate, buying guides, market trends, and home loan advice
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="px-6 md:px-12 lg:px-24 py-10">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No articles yet</h3>
              <p className="text-slate-500">Check back soon for new content</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
              {posts.map(post => (
                <article
                  key={post.id}
                  onClick={() => navigate(`/blog/${post.slug || post.id}`)}
                  className="bg-white rounded-xl border border-slate-100 overflow-hidden cursor-pointer group hover:shadow-lg hover:border-slate-200 transition-all"
                  data-testid={`blog-card-${post.id}`}
                >
                  <div className="h-44 bg-gradient-to-br from-slate-100 to-slate-50 overflow-hidden">
                    {post.cover_image ? (
                      <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-900">
                        <BookOpen className="w-10 h-10 text-white/30" />
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${CATEGORY_COLORS[post.category] || 'bg-slate-100 text-slate-700'}`}>
                        {CATEGORY_LABELS[post.category] || post.category}
                      </span>
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {estimateReadTime(post.content)} min
                      </span>
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-sm text-slate-500 line-clamp-2 mb-4">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <div className="flex items-center gap-1">
                        <User className="w-3.5 h-3.5" />
                        <span>{post.author} · {new Date(post.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Blog;
